import React, { useState } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import Papa from "papaparse";

// Import helpers
import { getFormattedCurrentDateYYYYMMDD } from "./handlers/helpers";

const SQLQueryProcessor = ({ soapCredentials, onBackToTools }) => {
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collectedData, setCollectedData] = useState({});
  const [currentQuery, setCurrentQuery] = useState("");
  const [sqlResults, setSqlResults] = useState("");
  const [error, setError] = useState(null);

  const orgid = soapCredentials?.org_id || null;

  // Copy SQL query to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      addProgressMessage("SQL query copied to clipboard!", "success");
    } catch (err) {
      addProgressMessage("Failed to copy SQL query", "error");
      console.error("Failed to copy: ", err);
    }
  };


  // Define all required data collection steps
  const dataCollectionSteps = {
    // Core Contact Center Entities
    sites: {
      name: "Sites",
      description: "Collect customer definition/site information from AWDB",
      excelSheet: "Sites", // Matches actual sheet name
      sqlQuery: `SELECT 
        EnterpriseName,
        'Default_Telephony_Profile' as "Media Profile"
      FROM [AWDB].[dbo].[t_Customer_Definition] cd  -- Note: matches the CCE table name shown in A1
      ORDER BY cd.EnterpriseName`,
      expectedColumns: ["EnterpriseName"],
      columnMapping: {
        "Site Name": "Site Name", // Maps to actual Excel column header "Site Name" (not "Name")
        "Media Profile": "Media Profile" // Maps to actual Excel column header
      },
      transformFunction: (data) => {
        addProgressMessage(`Transforming ${data.length} Site records`, "info");
        return data.map((row) => ({
          "Site Name": row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN",
          "Media Profile": row["Media Profile"] || row["mediaprofile"] || "Default_Telephony_Profile"
        }));
      },
      completed: false,
      data: null,
    },
    skillDefinitions: {
      name: "Skill Definitions",
      description: "Collect skill definitions from Skill Groups with NULL PrecisionQueueID",
      excelSheet: "Skill Definitions", // Matches actual sheet name
      sqlQuery: `SELECT PeripheralName, PrecisionQueueID 
FROM [AWDB].[dbo].[t_Skill_Group] sg 
WHERE PrecisionQueueID IS NULL 
ORDER BY sg.PeripheralName`,
      expectedColumns: ["PeripheralName", "PrecisionQueueID"],
      columnMapping: {
        "Skill Name": "A",
        "Description": "B", 
        "Service Level Threshold": "C",
        "Type (Text, Proficiency, Boolean, Enum)": "D",
        "Enum Value(s)": "E"
      },
      transformFunction: (data, collectedData) => {
        addProgressMessage(
          `Processing ${data.length} Skill Definitions from Skill Groups`,
          "info"
        );
        
        // Get attributes data to include as additional skill definitions
        const attributesData = collectedData?.attributesData?.data || [];
        
        addProgressMessage(`DEBUGGING: collectedData keys: ${Object.keys(collectedData || {})}`, "info");
        addProgressMessage(`DEBUGGING: attributesData available: ${!!collectedData?.attributesData}, length: ${attributesData.length}`, "info");
        addProgressMessage(`Found ${attributesData.length} Attributes to include`, "info");
        
        // Create skill definitions from skill groups
        const skillGroupSkills = data.map((row) => ({
          "Skill Name": row.PeripheralName || row.peripheralname || row.name || "UNKNOWN",
          "Description": `Skill for ${row.PeripheralName || row.peripheralname || row.name || "UNKNOWN"}`,
          "Service Level Threshold": 30,
          "Type (Text, Proficiency, Boolean, Enum)": "PROFICIENCY",
          "Enum Value(s)": "" // Remove enum values as requested
        }));
        
        // Create skill definitions from attributes
        const attributeSkills = attributesData.map((attr) => ({
          "Skill Name": attr.EnterpriseName || attr.enterprisename || attr.name || "UNKNOWN",
          "Description": `Attribute skill for ${attr.EnterpriseName || attr.enterprisename || attr.name || "UNKNOWN"}`,
          "Service Level Threshold": 30,
          "Type (Text, Proficiency, Boolean, Enum)": "PROFICIENCY",
          "Enum Value(s)": ""
        }));
        
        // Combine both sets of skills
        const allSkills = [...skillGroupSkills, ...attributeSkills];
        
        addProgressMessage(`Created ${skillGroupSkills.length} Skill Group skills and ${attributeSkills.length} Attribute skills (${allSkills.length} total)`, "success");
        
        return allSkills;
      },
      completed: false,
      data: null,
      additionalInstructions: "Gets skill definitions from Skill Groups where PrecisionQueueID is NULL"
    },
    teams: {
      name: "Teams",
      description: "Collect agent team information from AWDB",
      excelSheet: "Teams", // Matches actual sheet name
      sqlQuery: `SELECT 
        EnterpriseName
      FROM [AWDB].[dbo].[t_Agent_Team] at
      ORDER BY at.EnterpriseName`,
      expectedColumns: ["EnterpriseName"],
      columnMapping: {
        "Name": "A", // Team Name
        "Type": "B", // Team Type  
        "Site": "C", // Site Name
        "Media Profile": "D", // Media Profile
        "Skill Profile": "E" // Skill Profile
      },
      transformFunction: (data) => {
        addProgressMessage(`Transforming ${data.length} Team records`, "info");
        return data.map((row) => ({
          "Name": row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN",
          "Type": "AGENT", // Default value
          "Site": "Default_Site", // Default value
          "Media Profile": "TELEPHONY", // Default value
          "Skill Profile": "Default_Profile" // Default value
        }));
      },
      completed: false,
      data: null,
    },
    queues: {
      name: "Queues",
      description: "Collect all skill groups/queues from AWDB",
      excelSheet: "Queues", // Matches actual sheet name
      sqlQuery: `SELECT PrecisionQueueID, PeripheralName 
FROM [AWDB].[dbo].[t_Skill_Group] sg
ORDER BY sg.PeripheralName`,
      expectedColumns: ["PrecisionQueueID", "PeripheralName"],
      columnMapping: {
        "Name": "A",
        "Direction": "B", 
        "Channel Type": "C",
        "Service Level Threshold": "D",
        "Max Time in Queue": "E",
        "Routing Type": "F"
      },
      transformFunction: (data, collectedData) => {
        addProgressMessage(
          `Processing ${data.length} SkillGroup records, but prioritizing PQ data for Queues`,
          "info"
        );
        
        // Get precision queues data as the primary source for queues
        const precisionQueuesData = collectedData?.precisionQueuesData?.data || [];
        
        addProgressMessage(`DEBUGGING Queues: collectedData keys: ${Object.keys(collectedData || {})}`, "info");
        addProgressMessage(`DEBUGGING Queues: precisionQueuesData available: ${!!collectedData?.precisionQueuesData}, length: ${precisionQueuesData.length}`, "info");
        addProgressMessage(`Found ${precisionQueuesData.length} Precision Queues to use as primary queue data`, "info");
        
        // Create queues from PQ data first
        const pqQueues = precisionQueuesData.map((pq) => {
          const queueName = pq.EnterpriseName || pq.enterprisename || pq.name || "UNKNOWN";
          
          return {
            "Name": queueName,
            "Direction": "INBOUND",
            "Channel Type": "TELEPHONY", 
            "Service Level Threshold": "30",
            "Max Time in Queue": "7200",
            "Routing Type": "Skill Based" // PQs are skill-based by definition
          };
        });
        
        // Also include skill groups that don't have PQs (NULL PrecisionQueueID)
        const skillGroupQueues = data
          .filter(row => !row.PrecisionQueueID || row.PrecisionQueueID === 'NULL' || row.PrecisionQueueID === '')
          .map((row) => {
            const queueName = row.PeripheralName || row.peripheralname || row.name || "UNKNOWN";
            
            return {
              "Name": queueName,
              "Direction": "INBOUND",
              "Channel Type": "TELEPHONY", 
              "Service Level Threshold": "30",
              "Max Time in Queue": "7200",
              "Routing Type": "Longest Available" // Skill groups without PQ are longest available
            };
          });
        
        // Combine PQ queues and skill group queues
        const allQueues = [...pqQueues, ...skillGroupQueues];
        
        addProgressMessage(`Created ${pqQueues.length} PQ-based queues and ${skillGroupQueues.length} SkillGroup-based queues (${allQueues.length} total)`, "success");
        
        return allQueues;
      },
      completed: false,
      data: null,
      additionalInstructions: "Single query to get all skill groups. Logic applied based on PrecisionQueueID value."
    },
    auxCodes: {
      name: "Auxiliary Codes",
      description: "Collect reason codes from AWDB",
      excelSheet: "Aux Codes", // Corrected to match actual sheet name
      sqlQuery: `SELECT 
        ReasonText,
        ReasonType
      FROM [AWDB].[dbo].[t_Reason_Code] 
      ORDER BY ReasonText`,
      expectedColumns: ["ReasonText", "ReasonType"],
      columnMapping: {
        "Code Name": "A",
        "Description": "B",
        "Type": "C"
      },
      transformFunction: (data) => {
        addProgressMessage(
          `Transforming ${data.length} Auxiliary Code records`,
          "info"
        );
        return data.map((row) => ({
          "Code Name": row["Reason Text"] || row.ReasonText || row.Name || row.name || "UNKNOWN",
          "Description": row["Reason Text"] || row.ReasonText || row.Name || row.name || "UNKNOWN", 
          "Type": row.Type === "Wrap Up Code" || row.ReasonType === "2" ? "Wrap Up Code" : "Idle Code",
        }));
      },
      completed: false,
      data: null,
    },
    desktopProfiles: {
      name: "Desktop Profiles",
      description: "Collect agent desk settings from AWDB",
      excelSheet: "Desktop Profiles", // Matches actual sheet name
      sqlQuery: `SELECT 
        DISTINCT 
        EnterpriseName,
        EnterpriseName as Name,
        AutoAnswerEnabled,
        WorkModeTimer
      FROM [AWDB].[dbo].[t_Agent_Desk_Settings] ads
      ORDER BY ads.EnterpriseName`,
      expectedColumns: ["EnterpriseName", "AutoAnswerEnabled", "WorkModeTimer"],
      columnMapping: {
        "Name": "A",
        "Type": "B",
        "Screen Pops": "C",
        "Last Agent Routing": "D",
        "Auto Answer": "E",
        "Wrap-Up Type": "F", 
        "If Auto, Time (Seconds)": "G"
      },
      transformFunction: (data) => {
        addProgressMessage(
          `Transforming ${data.length} Desktop Profile records`,
          "info"
        );
        return data.map((row) => ({
          "Name": row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN",
          "Type": "Agent", // Default value
          "Screen Pops": "Enabled", // Default value
          "Last Agent Routing": "Disabled", // Default value
          "Auto Answer": row.AutoAnswerEnabled === 'Y' || row.autoanswerenabled === 'Y' ? "Enabled" : "Disabled",
          "Wrap-Up Type": (row.WorkModeTimer || row.workmodetimer) !== 0 ? "AUTO" : "Manual",
          "If Auto, Time (Seconds)": (row.WorkModeTimer || row.workmodetimer) !== 0 ? (row.WorkModeTimer || row.workmodetimer) : ""
        }));
      },
      completed: false,
      data: null,
    },
    dialedNumbers: {
      name: "Dialed Numbers",
      description: "Collect all dialed numbers from AWDB and map to Entry Points",
      excelSheet: "Dialed Number Mappings", // Correct sheet name from template
      sqlQuery: `SELECT [DialedNumberID],[DialedNumberString] 
FROM [AWDB].[dbo].[t_Dialed_Number]
ORDER BY DialedNumberString`,
      expectedColumns: ["DialedNumberID", "DialedNumberString"],
      columnMapping: {
        "Number (DID/Toll Free)": "A",
        "LOB / Call Flow": "B",
        "Description": "C",
        "Max Active Calls": "D",
        "Entry Point": "E",
        "Notes": "F"
      },
      transformFunction: (data, collectedData) => {
        addProgressMessage(
          `Processing ${data.length} Dialed Number records with Entry Point mapping`,
          "info"
        );
        
        // Get mapping data to link dialed numbers to entry points
        const dialedNumberMapData = collectedData?.dialedNumberMapData?.data || [];
        const callTypesData = collectedData?.callTypesData?.data || [];
        
        addProgressMessage(`Found ${dialedNumberMapData.length} DN mappings and ${callTypesData.length} Call Types`, "info");
        
        // Create mapping: DialedNumberID -> CallTypeID -> EnterpriseName (Entry Point Name)
        const dialedNumberToCallType = {};
        dialedNumberMapData.forEach(mapping => {
          const dnId = (mapping.DialedNumberID || mapping.dialednumberid || "").toString();
          const ctId = (mapping.CallTypeID || mapping.calltypeid || "").toString();
          if (dnId && ctId) {
            dialedNumberToCallType[dnId] = ctId;
          }
        });
        
        const callTypeToEntryPoint = {};
        callTypesData.forEach(callType => {
          const ctId = (callType.CallTypeID || callType.calltypeid || "").toString();
          const entryPointName = callType.EnterpriseName || callType.enterprisename || callType.name;
          if (ctId && entryPointName) {
            callTypeToEntryPoint[ctId] = entryPointName;
          }
        });
        
        addProgressMessage(`Created mapping tables: ${Object.keys(dialedNumberToCallType).length} DN->CT, ${Object.keys(callTypeToEntryPoint).length} CT->EP`, "info");
        
        return data.map((row) => {
          const dialedNumberId = (row.DialedNumberID || row.dialednumberid || "").toString();
          const dialedNumberString = row.DialedNumberString || row.dialednumberstring || row.number || "UNKNOWN";
          
          // Find the entry point for this dialed number
          let entryPoint = "[To be mapped]"; // Default
          const callTypeId = dialedNumberToCallType[dialedNumberId];
          if (callTypeId) {
            const entryPointName = callTypeToEntryPoint[callTypeId];
            if (entryPointName) {
              entryPoint = entryPointName;
            }
          }
          
          return {
            "Number (DID/Toll Free)": dialedNumberString,
            "LOB / Call Flow": "Default",
            "Description": `DN for ${dialedNumberString}`,
            "Max Active Calls": 100,
            "Entry Point": entryPoint,
            "Notes": "Imported from AWDB"
          };
        });
      },
      completed: false,
      data: null,
      additionalInstructions: "Maps dialed numbers to entry points."
    },
    entryPoints: {
      name: "Entry Points",
      description: "Collect call types that are tied to dialed numbers",
      excelSheet: "Entry Points", // Matches actual sheet name
      sqlQuery: `SELECT [CallTypeID],[EnterpriseName]
FROM [AWDB].[dbo].[t_Call_Type]
ORDER BY EnterpriseName`,
      expectedColumns: ["CallTypeID", "EnterpriseName"],
      columnMapping: {
        "Name": "A",
        "Description": "B",
        "Direction": "C",
        "Channel": "D",
        "If Chat or Email, Asset ID": "E",
        "Service Level Threshold": "F"
      },
      transformFunction: (data, collectedData) => {
        addProgressMessage(
          `Processing ${data.length} Entry Point records - filtering by Dialed Number mapping`,
          "info"
        );
        
        // Get dialed number mapping data to filter entry points
        const dialedNumberMapData = collectedData?.dialedNumberMapData?.data || [];
        const callTypeMapData = collectedData?.callTypeMapData?.data || [];
        
        addProgressMessage(`Found ${dialedNumberMapData.length} Dialed Number mappings for filtering`, "info");
        
        // Create set of CallTypeIDs that are tied to dialed numbers
        const mappedCallTypeIds = new Set();
        
        // From DN_Map.csv: DialedNumberID -> CallTypeID
        dialedNumberMapData.forEach(mapping => {
          const callTypeId = mapping.CallTypeID || mapping.calltypeid;
          if (callTypeId) {
            mappedCallTypeIds.add(callTypeId.toString());
          }
        });
        
        // From CallType_Map.csv: CallTypeID -> MasterScriptID  
        callTypeMapData.forEach(mapping => {
          const callTypeId = mapping.CallTypeID || mapping.calltypeid;
          if (callTypeId) {
            mappedCallTypeIds.add(callTypeId.toString());
          }
        });
        
        addProgressMessage(`Found ${mappedCallTypeIds.size} unique CallTypeIDs tied to Dialed Numbers`, "info");
        
        // Filter Entry Points to only include those tied to dialed numbers
        const filteredData = data.filter(row => {
          const callTypeId = (row.CallTypeID || row.calltypeid || "").toString();
          return mappedCallTypeIds.has(callTypeId);
        });
        
        addProgressMessage(`Filtered from ${data.length} to ${filteredData.length} Entry Points tied to Dialed Numbers`, "success");
        
        return filteredData.map((row) => ({
          "Name": row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN",
          "Description": `Entry point for ${row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN"}`,
          "Direction": "INBOUND",
          "Channel": "TELEPHONY",
          "If Chat or Email, Asset ID": "",
          "Service Level Threshold": 60
        }));
      },
      completed: false,
      data: null,
      additionalInstructions: "Entry points from call types."
    },
    globalVariables: {
      name: "Global Variables",
      description: "Collect user variables from AWDB",
      excelSheet: "Global Variables", // Matches actual sheet name
      sqlQuery: `SELECT 
        VariableName,
        'STRING' as "Variable Type",
        COALESCE(DefaultValue, '') as "Default Value",
        COALESCE(Description, '') as Description
      FROM [AWDB].[dbo].[t_User_Variable] uv
      ORDER BY uv.VariableName`,
      expectedColumns: ["VariableName"],
      columnMapping: {
        "Name": "A",
        "Description": "B",
        "Agent Viewable": "C",
        "Agent Editable": "D",
        "Type": "E",
        "Default Value": "F",
        "Reportable": "G",
        "Desktop Label": "H"
      },
      transformFunction: (data) => {
        addProgressMessage(
          `Transforming ${data.length} Global Variable records`,
          "info"
        );
        return data.map((row) => ({
          "Name": row.VariableName || row.variablename || row.name || "UNKNOWN",
          "Description": row.Description || row.description || "",
          "Agent Viewable": "false",
          "Agent Editable": "false", 
          "Type": "String",
          "Default Value": row["Default Value"] || row.defaultvalue || "",
          "Reportable": "false",
          "Desktop Label": ""
        }));
      },
      completed: false,
      data: null,
    },
    desktopLayouts: {
      name: "Desktop Layouts",
      description: "Collect desktop layout definitions from AWDB",
      excelSheet: "Desktop Layouts", // Matches actual sheet name
      sqlQuery: `SELECT 
        EnterpriseName
      FROM [AWDB].[dbo].[t_Agent_Desk_Settings] ads
      ORDER BY ads.EnterpriseName`,
      expectedColumns: ["EnterpriseName"],
      columnMapping: {
        "NAME": "A",
        "Description": "B",
        "JSON File Name": "C",
        "Team": "D"
      },
      transformFunction: (data) => {
        addProgressMessage(
          `Processing ${data.length} Desktop Layout records`,
          "info"
        );
        return data.map((row) => ({
          "NAME": row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN",
          "Description": "Desktop layout for " + (row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN"),
          "JSON File Name": "",
          "Team": ""
        }));
      },
      completed: false,
      data: null,
    },
    businessHours: {
      name: "Business Hours",
      description: "Collect business hours configuration from AWDB",
      excelSheet: "Hours of Operation", // Corrected to match actual sheet name
      sqlQuery: `SELECT 
        BusinessHoursID, 
        EnterpriseName,
        EnterpriseName as Name,
        BusinessHourType
      FROM [AWDB].[dbo].[t_Business_Hours] bh
      ORDER BY bh.BusinessHoursID`,
      expectedColumns: ["EnterpriseName", "BusinessHoursID", "BusinessHourType"],
      columnMapping: {
        "Schedule Name": "A",
        "Description": "B",
        "Day of Week": "C",
        "Start Time": "D", 
        "End Time": "E",
        "Holidays": "F",
        "Overrides": "G"
      },
      transformFunction: (data, collectedData) => {
        addProgressMessage(
          `Transforming ${data.length} Business Hours records with Holiday mapping`,
          "info"
        );
        
        // Create schedule entries for each day of the week
        const scheduleEntries = [];
        const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        
        data.forEach((row) => {
          const scheduleName = row.EnterpriseName || row.enterprisename || row.name || "UNKNOWN";
          daysOfWeek.forEach((day) => {
            scheduleEntries.push({
              "Schedule Name": scheduleName,
              "Description": "Business hours for " + scheduleName,
              "Day of Week": day,
              "Start Time": "08:00", // Default - should be updated with actual schedule data
              "End Time": "17:00",    // Default - should be updated with actual schedule data
              "Holidays": `${scheduleName} List`, // Use Schedule Name + " List" for Holiday List Name
              "Overrides": "Default_Override_List"
            });
          });
        });
        
        return scheduleEntries;
      },
      completed: false,
      data: null,
      requiresAdditionalData: ["businessHoursSchedule"],
    },
    businessHoursSchedule: {
      name: "Business Hours Schedule",
      description: "Collect detailed schedule information from AWDB",
      excelSheet: null, // This data is used to enhance Business Hours
      sqlQuery: `SELECT BusinessHoursID, DayType, StartHour, StartMinute, StartSecond, EndHour, EndMinute, EndSecond
FROM [AWDB].[dbo].[t_Week_Day_Schedule]
ORDER BY BusinessHoursID, DayType`,
      expectedColumns: [
        "BusinessHoursID",
        "DayType",
        "StartHour",
        "StartMinute",
        "StartSecond",
        "EndHour",
        "EndMinute",
        "EndSecond",
      ],
      columnMapping: {},
      transformFunction: (data) => data,
      completed: false,
      data: null,
      isSupplementary: true,
    },
    overrides: {
      name: "Override Reasons",
      description: "Collect override reason definitions from AWDB",
      excelSheet: "Overrides",
      sqlQuery: `SELECT BusinessHourReasonID, ReasonText
FROM [AWDB].[dbo].[t_Business_Hours_Reason]
ORDER BY ReasonText`,
      expectedColumns: ["BusinessHourReasonID", "ReasonText"],
      columnMapping: {
        "Override List Name": "A",
        "Override Name": "B"
      },
      transformFunction: (data) => {
        addProgressMessage(
          `Transforming ${data.length} Override records`,
          "info"
        );
        return data.map((row) => ({
          "Override List Name": "Default_Override_List",
          "Override Name": row.ReasonText || row.reasontext || row.name || "UNKNOWN"
        }));
      },
      completed: false,
      data: null,
      isSupplementary: false,
    },
    holidaySchedule: {
      name: "Holiday Schedule",
      description: "Collect holiday schedule data from AWDB",
      excelSheet: "Holidays",
      sqlQuery: `SELECT SpecialDayScheduleID, BusinessHourReasonID, BusinessHoursID, Description, 
ScheduleStatus, SpecialDay, MonthOfSpecialDay, YearOfSpecialDay
FROM [AWDB].[dbo].[t_Special_Day_Schedule]
ORDER BY BusinessHoursID, MonthOfSpecialDay, SpecialDay`,
      expectedColumns: [
        "SpecialDayScheduleID",
        "BusinessHourReasonID",
        "BusinessHoursID",
        "Description",
        "ScheduleStatus",
        "SpecialDay",
        "MonthOfSpecialDay",
        "YearOfSpecialDay",
      ],
      columnMapping: {
        "Holiday List Name": "A",
        "Holiday": "B",
        "Date": "C"
      },
      transformFunction: (data, collectedData) => {
        addProgressMessage(
          `Transforming ${data.length} Holiday records with Business Hours mapping`,
          "info"
        );
        
        // Get RAW business hours data (not transformed) to map Holiday List Names
        const businessHoursRawData = collectedData?.businessHours?.originalData || [];
                                   
        // If we can't find raw data, let's try to extract from other loaded data
        const businessHoursData = businessHoursRawData.length > 0 ? businessHoursRawData : 
          (collectedData?.businessHoursSchedule?.data || []).reduce((acc, schedule) => {
            // Try to reconstruct from schedule data if needed
            const bhId = schedule.BusinessHoursID || schedule.businesshoursid;
            if (bhId && !acc.find(bh => bh.BusinessHoursID === bhId)) {
              acc.push({
                BusinessHoursID: bhId,
                EnterpriseName: `Schedule_${bhId}` // Fallback name
              });
            }
            return acc;
          }, []);
        
        addProgressMessage(`Found ${businessHoursData.length} Business Hours for mapping`, "info");
        
        // Create mapping: BusinessHoursID -> EnterpriseName (Schedule Name)
        const businessHoursIdToName = {};
        businessHoursData.forEach(bh => {
          const bhId = (bh.BusinessHoursID || bh.businesshoursid || "").toString();
          const scheduleName = bh.EnterpriseName || bh.enterprisename || bh.name;
          if (bhId && scheduleName) {
            businessHoursIdToName[bhId] = scheduleName;
          }
        });
        
        return data.map((row) => {
          // Format the date as MM/DD/YYYY
          const month = (row.MonthOfSpecialDay || "01").toString().padStart(2, "0");
          const day = (row.SpecialDay || "01").toString().padStart(2, "0");
          const year = row.YearOfSpecialDay || "2024";
          const dateString = `${month}/${day}/${year}`;
          
          // Map BusinessHoursID to Holiday List Name (Schedule Name + " List")
          const businessHoursId = (row.BusinessHoursID || row.businesshoursid || "").toString();
          const scheduleName = businessHoursIdToName[businessHoursId];
          const holidayListName = scheduleName ? `${scheduleName} List` : "Default_Holiday_List";
          
          return {
            "Holiday List Name": holidayListName,
            "Holiday": row.Description || row.description || "UNKNOWN",
            "Date": dateString
          };
        });
      },
      completed: false,
      data: null,
      requiresAdditionalData: ["businessHours"],
    },
    serviceLevelData: {
      name: "Service Level Threshold Data",
      description: "Collect Service Level Threshold for Queues and Skills where PrecisionQueueID matches",
      excelSheet: null, // This data updates both Queues and Skill Definitions sheets
      sqlQuery: `SELECT 
        PrecisionQueueID,
        PeripheralName,
        ServiceLevelThreshold,
        ServiceLevelType
      FROM [AWDB].[dbo].[t_Skill_Group] sg 
      WHERE PrecisionQueueID IS NOT NULL
      ORDER BY sg.PeripheralName`,
      expectedColumns: ["PrecisionQueueID", "PeripheralName", "ServiceLevelThreshold", "ServiceLevelType"],
      columnMapping: {}, // This will update other sheets
      transformFunction: (data) => {
        addProgressMessage(
          `Processing Service Level data for ${data.length} records - will update Queues and Skill Definitions sheets`,
          "info"
        );
        return data; // Return raw data for cross-referencing with other sheets
      },
      completed: false,
      data: null,
      isSupplementary: true,
      updatesMultipleSheets: ["Queues", "Skill Definitions"]
    },
    
    // Individual Query Collections for Complex Multi-Query Sections
    attributesData: {
      name: "Attributes Data",
      description: "Collect all attributes for Skill Definitions processing",
      excelSheet: null,
      sqlQuery: `SELECT [AttributeID],[EnterpriseName],[AttributeDataType] 
FROM [AWDB].[dbo].[t_Attribute]`,
      expectedColumns: ["AttributeID", "EnterpriseName", "AttributeDataType"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Attribute records for Skill Definitions`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    skillGroupsNullData: {
      name: "Skill Groups (NULL PrecisionQueueID)",
      description: "Collect skill groups with NULL PrecisionQueueID for Skill Definitions",
      excelSheet: null,
      sqlQuery: `SELECT PeripheralName, PrecisionQueueID 
FROM [AWDB].[dbo].[t_Skill_Group] sg 
WHERE PrecisionQueueID IS NULL 
ORDER BY sg.PeripheralName`,
      expectedColumns: ["PeripheralName", "PrecisionQueueID"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Skill Group records (NULL PrecisionQueueID)`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    precisionQueuesData: {
      name: "Precision Queues Data",
      description: "Collect precision queue definitions for Queue processing",
      excelSheet: null,
      sqlQuery: `SELECT [PrecisionQueueID],[EnterpriseName] 
FROM [AWDB].[dbo].[t_Precision_Queue]`,
      expectedColumns: ["PrecisionQueueID", "EnterpriseName"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Precision Queue records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    precisionQueueStepsData: {
      name: "Precision Queue Steps Data",
      description: "Collect precision queue step definitions for Queue processing",
      excelSheet: null,
      sqlQuery: `SELECT [PrecisionQueueStepID],[PrecisionQueueID],[StepOrder],[WaitTime]
FROM [AWDB].[dbo].[t_Precision_Queue_Step]`,
      expectedColumns: ["PrecisionQueueStepID", "PrecisionQueueID", "StepOrder", "WaitTime"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Precision Queue Step records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    precisionQueueTermsData: {
      name: "Precision Queue Terms Data", 
      description: "Collect precision queue term definitions for Queue processing",
      excelSheet: null,
      sqlQuery: `SELECT [PrecisionQueueTermID],[PrecisionQueueStepID],[PrecisionQueueID],[AttributeID]
FROM [AWDB].[dbo].[t_Precision_Queue_Term]`,
      expectedColumns: ["PrecisionQueueTermID", "PrecisionQueueStepID", "PrecisionQueueID", "AttributeID"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Precision Queue Term records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    callTypesData: {
      name: "Call Types Data",
      description: "Collect call type definitions for Entry Points and Dialed Numbers processing",
      excelSheet: null,
      sqlQuery: `SELECT [CallTypeID],[EnterpriseName]
FROM [AWDB].[dbo].[t_Call_Type]`,
      expectedColumns: ["CallTypeID", "EnterpriseName"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Call Type records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    callTypeMapData: {
      name: "Call Type Map Data",
      description: "Collect call type mapping for Entry Points and Dialed Numbers processing",
      excelSheet: null,
      sqlQuery: `SELECT [CallTypeID],[MasterScriptID]
FROM [AWDB].[dbo].[t_Call_Type_Map]`,
      expectedColumns: ["CallTypeID", "MasterScriptID"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Call Type Map records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    dialedNumberMapData: {
      name: "Dialed Number Map Data",
      description: "Collect dialed number mapping for Entry Points and Dialed Numbers processing",
      excelSheet: null,
      sqlQuery: `SELECT [DialedNumberID],[CallTypeID]
FROM [AWDB].[dbo].[t_Dialed_Number_Map]`,
      expectedColumns: ["DialedNumberID", "CallTypeID"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Dialed Number Map records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
    
    dialedNumbersBaseData: {
      name: "Dialed Numbers Base Data",
      description: "Collect base dialed number definitions for Entry Points and Dialed Numbers processing",
      excelSheet: null,
      sqlQuery: `SELECT [DialedNumberID],[DialedNumberString] 
FROM [AWDB].[dbo].[t_Dialed_Number]`,
      expectedColumns: ["DialedNumberID", "DialedNumberString"],
      columnMapping: {},
      transformFunction: (data) => {
        addProgressMessage(`Collected ${data.length} Dialed Number Base records`, "info");
        return data;
      },
      completed: false,
      data: null,
      isSupplementary: true
    },
  };

  // Helper function to add progress messages
  const addProgressMessage = (message, type = "info") => {
    setProgress((prev) => [
      ...prev,
      { type, message, timestamp: new Date().toLocaleTimeString() },
    ]);
  };

  // Note: File upload functionality removed - using paste-only workflow

  // Process manual SQL paste data (commented out - not currently used)
  // const processManualData = () => {
  //   addProgressMessage("Manual data processing not implemented yet", "info");
  // };

  // Load test data with sample data for testing
  const loadTestData = async (testDataFile = null, manualCollectedData = null) => {
    console.log('Loading test data...');
    setLoading(true);
    setError(null);
    
    try {
      // Check if we have manual data passed in
      if (manualCollectedData) {
        console.log('Using manual collected data:', manualCollectedData);
        setCollectedData(manualCollectedData);
        setLoading(false);
        return;
      }

      const csvFileMapping = {
        // Load supplementary data first (required for custom logic)
        attributesData: 'Attributes.csv',
        callTypesData: 'CallTypes.csv',
        callTypeMapData: 'CallType_Map.csv',
        dialedNumberMapData: 'DN_Map.csv',
        precisionQueuesData: 'PQ.csv',
        precisionQueueStepsData: 'PQ_Step.csv',
        precisionQueueTermsData: 'PQ_Term.csv',
        serviceLevelData: 'SkillGroup_PQ.csv',
        skillGroupsNullData: 'SkillGroups.csv',
        dialedNumbersBaseData: 'DN.csv',
        businessHoursSchedule: 'businessHoursSchedule.csv',
        
        // Main data processing (can now access supplementary data)
        sites: 'Sites.csv',
        teams: 'Teams.csv', 
        businessHours: 'businessHours.csv',
        skillDefinitions: 'SkillGroups.csv', // From SkillGroups where PrecisionQueueID is NULL
        queues: 'SkillGroups.csv', // From SkillGroups 
        auxCodes: 'ReasonCodes.csv',
        desktopProfiles: 'ADS.csv',
        dialedNumbers: 'DN.csv',
        entryPoints: 'CallTypes.csv',
        globalVariables: 'GlobalVariables.csv',
        desktopLayouts: 'ADS.csv',
        overrides: 'overRides.csv',
        holidaySchedule: 'holidaySchedule.csv'
      };

      const testData = {};
      console.log('Starting to load CSV files...');

      for (const [key, filename] of Object.entries(csvFileMapping)) {
        try {
          const filepath = testDataFile || `/sql_responses/${filename}`;
          console.log(`Loading ${filepath}...`);
          const response = await fetch(filepath);
          if (!response.ok) {
            console.log(`Failed to load ${filename}: ${response.status}`);
            testData[key] = [];
            continue;
          }
          const csvText = await response.text();
          const parsed = Papa.parse(csvText, { 
            header: true, 
            skipEmptyLines: true,
            transformHeader: (header) => header.trim()
          });
          testData[key] = parsed.data;
          console.log(`Loaded ${filename}: ${parsed.data.length} rows`);
        } catch (error) {
          console.error(`Error loading ${filename}:`, error);
          testData[key] = [];
        }
      }

      // Build up collected data as we process each step so later steps can access earlier data
      let buildingCollectedData = { ...collectedData };
      
      for (const [stepKey, data] of Object.entries(testData)) {
        try {
          if (dataCollectionSteps[stepKey] && data.length > 0) {
            const step = dataCollectionSteps[stepKey];
            const transformedData = step.transformFunction(data, buildingCollectedData);
            
            // Update building collected data for next iteration - PRESERVE ORIGINAL DATA
            buildingCollectedData[stepKey] = {
              ...step,
              completed: true,
              data: transformedData,
              originalData: data, // Preserve original raw CSV data
              finalProcessed: true
            };
            
            setCollectedData(prev => ({
              ...prev,
              [stepKey]: {
                ...step,
                completed: true,
                data: transformedData,
                originalData: data, // Preserve original raw CSV data
                finalProcessed: true
              }
            }));
            
            console.log(`Successfully processed ${stepKey}: ${transformedData.length} items`);
          }
        } catch (error) {
          console.error(`Error processing ${stepKey}:`, error);
          setError(error.message);
        }
      }

      console.log('All test data loaded successfully');
    } catch (error) {
      console.error('Error loading test data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };;

  // Note: Auto-import functionality removed - all SQL data must be provided manually

  // Parse CSV-like text input into array of objects
  const parseCSVInput = (csvText) => {
    if (!csvText.trim()) return [];

    const lines = csvText.trim().split("\n");
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const values = line.split(",").map((v) => v.trim());
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || "";
      });
      return obj;
    });

    return rows;
  };

  // Process Business Hours with schedule data - Create one row per day per business hours
  const processBusinessHoursData = (businessHoursData, scheduleData) => {
    if (!businessHoursData || !scheduleData) return businessHoursData;

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const processedRows = [];

    businessHoursData.forEach((bh) => {
      addProgressMessage(
        `Processing Business Hours: ${bh.EnterpriseName} (ID: ${bh.BusinessHoursID}, Type: ${bh.BusinessHourType})`,
        "info"
      );

      if (bh.BusinessHourType === "0") {
        // 24 hours - create one row for 24 hour operation
        processedRows.push({
          "Schedule Name": bh.EnterpriseName,
          "Day of Week": "All Days",
          "Start Time": "00:00",
          "End Time": "23:59",
        });
      } else if (bh.BusinessHourType === "1") {
        // Custom schedule - find matching schedule data
        const businessHoursID = bh.BusinessHoursID;
        const schedules = scheduleData.filter(
          (s) => s.BusinessHoursID.toString() === businessHoursID.toString()
        );

        addProgressMessage(
          `Found ${schedules.length} schedule entries for ${bh.EnterpriseName}`,
          "info"
        );

        // Create a map of existing schedule days
        const scheduleDayMap = {};
        schedules.forEach((schedule) => {
          scheduleDayMap[schedule.DayType] = schedule;
        });

        // Ensure all 7 days are covered (0=Sunday, 1=Monday, ..., 6=Saturday)
        for (let dayType = 0; dayType <= 6; dayType++) {
          const dayName = dayNames[dayType];

          if (scheduleDayMap[dayType]) {
            // Day has schedule data
            const schedule = scheduleDayMap[dayType];
            const startTime = `${schedule.StartHour.toString().padStart(
              2,
              "0"
            )}:${schedule.StartMinute.toString().padStart(2, "0")}`;
            const endTime = `${schedule.EndHour.toString().padStart(
              2,
              "0"
            )}:${schedule.EndMinute.toString().padStart(2, "0")}`;

            processedRows.push({
              "Schedule Name": bh.EnterpriseName,
              "Day of Week": dayName,
              "Start Time": startTime,
              "End Time": endTime,
            });
          } else {
            // Day has no schedule data - create blank entry
            processedRows.push({
              "Schedule Name": bh.EnterpriseName,
              "Day of Week": dayName,
              "Start Time": "",
              "End Time": "",
            });
          }
        }
      }
    });

    addProgressMessage(
      `Created ${processedRows.length} schedule rows from ${businessHoursData.length} business hours definitions`,
      "success"
    );
    return processedRows;
  };

  // Process Holiday data with business hours and override context
  const processHolidayData = (holidayData, businessHoursData, overrideData) => {
    if (!holidayData || !businessHoursData) {
      addProgressMessage(
        "Missing required data for holiday processing",
        "error"
      );
      return [];
    }

    addProgressMessage(
      `Processing ${holidayData.length} holiday records with ${businessHoursData.length} business hours definitions`,
      "info"
    );

    // Debug: Show what holiday and business hours data looks like
    addProgressMessage(
      `Holiday data sample: ${JSON.stringify(holidayData.slice(0, 1))}`,
      "info"
    );
    addProgressMessage(
      `Business Hours data sample: ${JSON.stringify(
        businessHoursData.slice(0, 1)
      )}`,
      "info"
    );

    // Group holiday data by BusinessHoursID to create separate holiday lists
    const holidayGroups = {};
    holidayData.forEach((holiday) => {
      const businessHoursID = holiday.BusinessHoursID;
      if (!holidayGroups[businessHoursID]) {
        holidayGroups[businessHoursID] = [];
      }
      holidayGroups[businessHoursID].push(holiday);
    });

    addProgressMessage(
      `Grouped holidays into ${
        Object.keys(holidayGroups).length
      } business hours groups`,
      "info"
    );

    // Create holiday list entries by correlating with business hours
    const holidayListEntries = [];
    Object.entries(holidayGroups).forEach(([businessHoursID, holidays]) => {
      const businessHour = businessHoursData.find(
        (bh) => bh.BusinessHoursID.toString() === businessHoursID.toString()
      );

      if (!businessHour) {
        addProgressMessage(
          `Warning: No business hours found for ID ${businessHoursID}, skipping ${holidays.length} holidays`,
          "warning"
        );
        return;
      }

      // Create Holiday List Name by appending '_Holidays' to Schedule Name (EnterpriseName)
      const scheduleBaseName =
        businessHour.EnterpriseName || `BusinessHours_${businessHoursID}`;
      const holidayListName = `${scheduleBaseName}_Holidays`;

      addProgressMessage(
        `Creating holiday list: "${holidayListName}" for ${holidays.length} holidays (Business Hours: ${businessHour.EnterpriseName})`,
        "info"
      );

      holidays.forEach((holiday) => {
        // Format the date as MM/DD/YYYY
        const month = holiday.MonthOfSpecialDay.toString().padStart(2, "0");
        const day = holiday.SpecialDay.toString().padStart(2, "0");
        const year = holiday.YearOfSpecialDay;
        const dateString = `${month}/${day}/${year}`;

        addProgressMessage(
          `  Adding holiday: ${holiday.Description} on ${dateString}`,
          "info"
        );

        holidayListEntries.push({
          "Holiday List Name": holidayListName,
          Holiday: holiday.Description,
          Date: dateString,
        });
      });
    });

    addProgressMessage(
      `Created ${holidayListEntries.length} holiday entries across ${
        Object.keys(holidayGroups).length
      } holiday lists`,
      "success"
    );
    return holidayListEntries;
  };

  // Enhance Business Hours with Holiday List Names after holidays are processed
  const enhanceBusinessHoursWithHolidayLists = (
    businessHoursData,
    holidayData
  ) => {
    if (!businessHoursData || !holidayData) return businessHoursData;

    addProgressMessage(
      "Enhancing Business Hours with Holiday List information...",
      "info"
    );

    // Create a map of Schedule Names to Holiday List Names
    const holidayListMap = {};
    holidayData.forEach((holiday) => {
      // Extract the base schedule name from Holiday List Name (remove '_Holidays')
      const holidayListName = holiday["Holiday List Name"];
      if (holidayListName && holidayListName.endsWith("_Holidays")) {
        const scheduleName = holidayListName.replace("_Holidays", "");
        holidayListMap[scheduleName] = holidayListName;
      }
    });

    addProgressMessage(
      `Found ${
        Object.keys(holidayListMap).length
      } holiday lists for schedule enhancement`,
      "info"
    );

    // Enhance each business hours row with its corresponding Holiday List Name
    const enhancedData = businessHoursData.map((row) => {
      const scheduleName = row["Schedule Name"];
      const holidayListName = holidayListMap[scheduleName] || "";

      return {
        ...row,
        "Holiday List Name": holidayListName,
      };
    });

    addProgressMessage(
      `Enhanced ${enhancedData.length} Business Hours rows with Holiday List information`,
      "success"
    );
    return enhancedData;
  };

  // These functions are now handled inline during data collection for better state management

  // Process and store SQL results for current query
  const processCurrentQuery = () => {
    if (!sqlResults.trim()) {
      addProgressMessage("Please paste your SQL query results", "error");
      return;
    }

    if (!currentQuery) {
      addProgressMessage("Please select a data collection step", "error");
      return;
    }

    try {
      const parsedData = parseCSVInput(sqlResults);

      if (parsedData.length === 0) {
        addProgressMessage("No valid data found in SQL results", "error");
        return;
      }

      const step = dataCollectionSteps[currentQuery];
      const dataColumns = Object.keys(parsedData[0]);
      const missingColumns = step.expectedColumns.filter(
        (field) => !dataColumns.includes(field)
      );

      if (missingColumns.length > 0) {
        addProgressMessage(
          `Missing required columns: ${missingColumns.join(
            ", "
          )}. Available columns: ${dataColumns.join(", ")}`,
          "error"
        );
        return;
      }

      // Transform data using step-specific function
      const transformedData = step.transformFunction(parsedData);

      // Store raw data for supplementary steps that don't directly populate Excel
      const dataToStore = step.isSupplementary ? parsedData : transformedData;

      // Update collected data
      setCollectedData((prev) => ({
        ...prev,
        [currentQuery]: {
          ...step,
          completed: true,
          data: dataToStore,
          transformedData: transformedData,
          collectedAt: new Date().toISOString(),
          rowCount: parsedData.length,
        },
      }));

      addProgressMessage(
        `Successfully collected ${parsedData.length} rows for ${step.name}`,
        "success"
      );

      // For steps that require additional data, check if we can now process them automatically
      // Use the updated state by wrapping in a closure that gets the latest state
      setTimeout(() => {
        if (currentQuery === "businessHoursSchedule") {
          setCollectedData((prevData) => {
            if (prevData["businessHours"]?.completed) {
              addProgressMessage(
                "Both Business Hours datasets collected - automatically processing schedule data...",
                "info"
              );
              // Trigger processing with current state
              const businessHoursData = prevData["businessHours"]?.data;
              const scheduleData = prevData["businessHoursSchedule"]?.data;

              if (businessHoursData && scheduleData) {
                const processedData = processBusinessHoursData(
                  businessHoursData,
                  scheduleData
                );
                addProgressMessage(
                  `Enhanced Business Hours data with schedule information - created ${processedData.length} schedule rows`,
                  "success"
                );

                return {
                  ...prevData,
                  businessHours: {
                    ...prevData.businessHours,
                    transformedData: processedData,
                    finalProcessed: true,
                  },
                };
              }
            }
            return prevData;
          });
        } else if (currentQuery === "businessHours") {
          setCollectedData((prevData) => {
            if (prevData["businessHoursSchedule"]?.completed) {
              addProgressMessage(
                "Both Business Hours datasets collected - automatically processing schedule data...",
                "info"
              );
              // Trigger processing with current state
              const businessHoursData = prevData["businessHours"]?.data;
              const scheduleData = prevData["businessHoursSchedule"]?.data;

              if (businessHoursData && scheduleData) {
                const processedData = processBusinessHoursData(
                  businessHoursData,
                  scheduleData
                );
                addProgressMessage(
                  `Enhanced Business Hours data with schedule information - created ${processedData.length} schedule rows`,
                  "success"
                );

                return {
                  ...prevData,
                  businessHours: {
                    ...prevData.businessHours,
                    transformedData: processedData,
                    finalProcessed: true,
                  },
                };
              }
            }
            return prevData;
          });
        } else if (currentQuery === "holidaySchedule") {
          setCollectedData((prevData) => {
            addProgressMessage(
              `Holiday processing check - businessHours completed: ${!!prevData[
                "businessHours"
              ]?.completed}`,
              "info"
            );

            if (prevData["businessHours"]?.completed) {
              addProgressMessage(
                "Holiday dependencies collected - automatically processing holiday data...",
                "info"
              );
              // Process holidays with business hours data
              const holidayData = prevData["holidaySchedule"]?.data;
              const businessHoursData = prevData["businessHours"]?.data;

              if (holidayData && businessHoursData) {
                const processedData = processHolidayData(
                  holidayData,
                  businessHoursData,
                  null
                );
                addProgressMessage(
                  `Processed Holiday data into ${processedData.length} holiday list entries`,
                  "success"
                );

                // Also enhance Business Hours with Holiday List Names if Business Hours is already processed
                let updatedBusinessHours = { ...prevData.businessHours };
                if (
                  prevData["businessHours"]?.finalProcessed &&
                  prevData["businessHours"]?.transformedData
                ) {
                  const enhancedBusinessHours =
                    enhanceBusinessHoursWithHolidayLists(
                      prevData["businessHours"].transformedData,
                      processedData
                    );

                  updatedBusinessHours = {
                    ...prevData.businessHours,
                    transformedData: enhancedBusinessHours,
                    finalProcessed: true,
                    holidayEnhanced: true,
                  };
                }

                return {
                  ...prevData,
                  businessHours: updatedBusinessHours,
                  holidaySchedule: {
                    ...prevData.holidaySchedule,
                    transformedData: processedData,
                    finalProcessed: true,
                  },
                };
              } else {
                addProgressMessage(
                  `Holiday processing failed - missing data: holidayData=${!!holidayData}, businessHoursData=${!!businessHoursData}`,
                  "warning"
                );
              }
            } else {
              addProgressMessage(
                "Holiday dependencies not yet completed - will process when all data is available",
                "info"
              );
            }
            return prevData;
          });
        }

        // Additional check: If any step has dependencies but hasn't been processed yet, try to process it
        setCollectedData((prevData) => {
          let updated = { ...prevData };

          // Check if holidaySchedule needs processing
          if (
            prevData["holidaySchedule"]?.completed &&
            !prevData["holidaySchedule"]?.finalProcessed
          ) {
            if (prevData["businessHours"]?.completed) {
              addProgressMessage(
                "Found unprocessed holiday data - processing now...",
                "info"
              );

              const holidayData = prevData["holidaySchedule"]?.data;
              const businessHoursData = prevData["businessHours"]?.data;

              if (holidayData && businessHoursData) {
                const processedData = processHolidayData(
                  holidayData,
                  businessHoursData,
                  null
                );
                addProgressMessage(
                  `Late-processed Holiday data into ${processedData.length} holiday list entries`,
                  "success"
                );

                // Also enhance Business Hours with Holiday List Names if Business Hours is already processed
                if (
                  prevData["businessHours"]?.finalProcessed &&
                  prevData["businessHours"]?.transformedData
                ) {
                  const enhancedBusinessHours =
                    enhanceBusinessHoursWithHolidayLists(
                      prevData["businessHours"].transformedData,
                      processedData
                    );

                  updated.businessHours = {
                    ...prevData.businessHours,
                    transformedData: enhancedBusinessHours,
                    finalProcessed: true,
                    holidayEnhanced: true,
                  };
                }

                updated.holidaySchedule = {
                  ...prevData.holidaySchedule,
                  transformedData: processedData,
                  finalProcessed: true,
                };
              }
            }
          }

          return updated;
        });
      }, 50);

      // Also trigger holiday processing when business hours are completed
      if (currentQuery === "businessHours") {
        setTimeout(() => {
          setCollectedData((prevData) => {
            // Check if we can now process holidays
            if (
              prevData["holidaySchedule"]?.completed &&
              !prevData["holidaySchedule"]?.finalProcessed
            ) {
              if (prevData["businessHours"]?.completed) {
                addProgressMessage(
                  "Holiday dependencies now available - processing holiday data...",
                  "info"
                );

                const holidayData = prevData["holidaySchedule"]?.data;
                const businessHoursData = prevData["businessHours"]?.data;

                if (holidayData && businessHoursData) {
                  const processedData = processHolidayData(
                    holidayData,
                    businessHoursData,
                    null
                  );
                  addProgressMessage(
                    `Dependency-triggered Holiday processing: ${processedData.length} holiday list entries`,
                    "success"
                  );

                  // Also enhance Business Hours with Holiday List Names if Business Hours is already processed
                  let updatedBusinessHours = { ...prevData.businessHours };
                  if (
                    prevData["businessHours"]?.finalProcessed &&
                    prevData["businessHours"]?.transformedData
                  ) {
                    const enhancedBusinessHours =
                      enhanceBusinessHoursWithHolidayLists(
                        prevData["businessHours"].transformedData,
                        processedData
                      );

                    updatedBusinessHours = {
                      ...prevData.businessHours,
                      transformedData: enhancedBusinessHours,
                      finalProcessed: true,
                      holidayEnhanced: true,
                    };
                  }

                  return {
                    ...prevData,
                    businessHours: updatedBusinessHours,
                    holidaySchedule: {
                      ...prevData.holidaySchedule,
                      transformedData: processedData,
                      finalProcessed: true,
                    },
                  };
                }
              }
            }
            return prevData;
          });
        }, 150);
      }

      // Clear current inputs
      setSqlResults("");

      // Auto-select next incomplete step
      const nextStep = Object.keys(dataCollectionSteps).find(
        (key) => !collectedData[key]?.completed && key !== currentQuery
      );
      if (nextStep) {
        setCurrentQuery(nextStep);
        addProgressMessage(
          `Ready for next step: ${dataCollectionSteps[nextStep].name}`,
          "info"
        );
      } else {
        addProgressMessage(
          "All data collection steps completed! Ready to generate Excel file.",
          "success"
        );
        setCurrentQuery("");
      }
    } catch (error) {
      addProgressMessage(`Error processing data: ${error.message}`, "error");
    }
  };

  // Generate Excel file using ExcelJS for better formatting preservation
  const generateExcelTemplateWithStyling = async () => {
    if (Object.keys(collectedData).length === 0) {
      addProgressMessage(
        "No data collected yet. Please collect at least one dataset.",
        "error"
      );
      return;
    }

    setLoading(true);
    addProgressMessage(
      "Generating Excel template with preserved styling using ExcelJS...",
      "info"
    );

    try {
      // Load the original template using ExcelJS
      const templatePath = "/wxcc_files/wxcc_mock_ddd.xlsx";
      addProgressMessage(`Loading template from: ${templatePath}`, "info");
      const response = await fetch(templatePath);

      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();

      try {
        await workbook.xlsx.load(arrayBuffer);
      } catch (loadError) {
        addProgressMessage(
          `Warning: Template load had issues: ${loadError.message}`,
          "warning"
        );
        // Try to continue anyway
      }

      addProgressMessage(
        `Template loaded with sheets: ${workbook.worksheets
          .map((ws) => ws.name)
          .join(", ")}`,
        "success"
      );

      // Get list of sheets we plan to modify
      const sheetsToModify = Object.values(collectedData)
        .filter((stepData) => stepData.completed && stepData.excelSheet)
        .map((stepData) => stepData.excelSheet);

      addProgressMessage(
        `Planning to modify sheets: ${sheetsToModify.join(", ")}`,
        "info"
      );

      // Process each collected dataset
      Object.entries(collectedData).forEach(([stepKey, stepData]) => {
        addProgressMessage(
          `Checking step: ${stepKey}, completed: ${stepData.completed}, excelSheet: ${stepData.excelSheet}, finalProcessed: ${stepData.finalProcessed}`,
          "info"
        );

        if (
          stepData.completed &&
          stepData.excelSheet &&
          (stepData.transformedData || stepData.data)
        ) {
          addProgressMessage(
            `Processing ${stepData.name} data with ExcelJS...`,
            "info"
          );

          let worksheet;
          try {
            worksheet = workbook.getWorksheet(stepData.excelSheet);
          } catch (getSheetError) {
            addProgressMessage(
              `Error accessing sheet "${stepData.excelSheet}": ${getSheetError.message}`,
              "error"
            );
            return;
          }

          if (!worksheet) {
            addProgressMessage(
              `Warning: Sheet "${
                stepData.excelSheet
              }" not found. Available sheets: ${workbook.worksheets
                .map((ws) => ws.name)
                .join(", ")}`,
              "warning"
            );
            return;
          }

          // Skip processing if this is a complex sheet that might cause corruption
          if (stepData.excelSheet.toLowerCase().includes("cover")) {
            addProgressMessage(
              `Skipping complex sheet "${stepData.excelSheet}" to avoid corruption`,
              "warning"
            );
            return;
          }

          // Use finalProcessed data if available, otherwise use regular transformedData or data
          const dataToProcess = stepData.finalProcessed
            ? stepData.data
            : stepData.transformedData || stepData.data;

          addProgressMessage(
            `Using ${
              stepData.finalProcessed ? "finalProcessed" : "regular"
            } data. Available data types: transformedData=${!!stepData.transformedData}, data=${!!stepData.data}, finalProcessed=${
              stepData.finalProcessed
            }`,
            "info"
          );
          addProgressMessage(
            `Data to process: ${JSON.stringify(
              dataToProcess?.slice(0, 2) || []
            )} (showing first 2 rows)`,
            "info"
          );

          // Additional debug for Business Hours
          if (stepKey === "businessHours") {
            addProgressMessage(
              `Business Hours Debug - finalProcessed: ${stepData.finalProcessed}, transformedData length: ${stepData.transformedData?.length}, data length: ${stepData.data?.length}`,
              "info"
            );
          }

          if (!dataToProcess || dataToProcess.length === 0) {
            addProgressMessage(
              `No data to process for ${stepData.name}`,
              "warning"
            );
            return;
          }

          try {
            // Find header row and column positions
            let headerRow = -1;
            let columnPositions = {};
            let actualHeaders = [];

            // Search first 20 rows for headers and collect actual header names
            for (let r = 1; r <= Math.min(worksheet.rowCount, 20); r++) {
              const row = worksheet.getRow(r);
              const rowHeaders = [];
              row.eachCell((cell, colNumber) => {
                if (cell.value) {
                  const cellValue = cell.value.toString().trim();
                  rowHeaders.push(`Col${colNumber}: "${cellValue}"`);
                  Object.entries(stepData.columnMapping).forEach(
                    ([dataKey, excelColumn]) => {
                      if (cellValue === dataKey) {
                        columnPositions[dataKey] = colNumber;
                        headerRow = r;
                      }
                    }
                  );
                }
              });
              if (rowHeaders.length > 0) {
                actualHeaders.push(`Row${r}: [${rowHeaders.join(", ")}]`);
              }
            }

            // Log actual headers found for debugging
            addProgressMessage(
              `Sheet "${stepData.excelSheet}" actual headers: ${actualHeaders.join(" | ")}`,
              "info"
            );

            addProgressMessage(
              `Found header row at ${headerRow}, column positions: ${JSON.stringify(
                columnPositions
              )}`,
              "info"
            );

            if (headerRow === -1) {
              addProgressMessage(
                `Could not find headers for ${stepData.name}`,
                "warning"
              );
              return;
            }

            // Find the last row with data
            let lastRow = headerRow;
            for (let r = worksheet.rowCount; r > headerRow; r--) {
              const row = worksheet.getRow(r);
              let hasData = false;
              row.eachCell((cell) => {
                if (
                  cell.value !== null &&
                  cell.value !== undefined &&
                  cell.value !== ""
                ) {
                  hasData = true;
                }
              });
              if (hasData) {
                lastRow = r;
                break;
              }
            }

            // Insert data preserving formatting more carefully
            dataToProcess.forEach((rowData, rowIndex) => {
              const targetRowNumber = lastRow + 1 + rowIndex;
              const targetRow = worksheet.getRow(targetRowNumber);

              // First, try to find an existing data row to copy formatting from
              let templateFormatRow = null;
              for (let r = headerRow + 1; r <= lastRow; r++) {
                const checkRow = worksheet.getRow(r);
                let hasContent = false;
                checkRow.eachCell((cell) => {
                  if (
                    cell.value !== null &&
                    cell.value !== undefined &&
                    cell.value !== ""
                  ) {
                    hasContent = true;
                  }
                });
                if (hasContent) {
                  templateFormatRow = checkRow;
                  break;
                }
              }

              Object.entries(stepData.columnMapping).forEach(
                ([dataKey, excelColumn]) => {
                  const colNumber = columnPositions[dataKey];
                  if (colNumber && rowData[dataKey] !== undefined) {
                    const cell = targetRow.getCell(colNumber);

                    // Copy formatting from template row if available
                    if (templateFormatRow) {
                      const templateCell = templateFormatRow.getCell(colNumber);
                      if (templateCell && templateCell.style) {
                        try {
                          // Create a clean copy of the style to avoid circular references
                          const cleanStyle = {
                            font: templateCell.style.font
                              ? { ...templateCell.style.font }
                              : undefined,
                            alignment: templateCell.style.alignment
                              ? { ...templateCell.style.alignment }
                              : undefined,
                            border: templateCell.style.border
                              ? { ...templateCell.style.border }
                              : undefined,
                            fill: templateCell.style.fill
                              ? { ...templateCell.style.fill }
                              : undefined,
                            numFmt: templateCell.style.numFmt,
                          };

                          // Remove undefined properties
                          Object.keys(cleanStyle).forEach((key) => {
                            if (cleanStyle[key] === undefined) {
                              delete cleanStyle[key];
                            }
                          });

                          cell.style = cleanStyle;
                        } catch (styleError) {
                          addProgressMessage(
                            `Warning: Could not copy style for cell ${colNumber}: ${styleError.message}`,
                            "warning"
                          );
                        }
                      }
                    }

                    // Set the value - ensure it's a proper type
                    let cellValue = rowData[dataKey];
                    if (cellValue === null || cellValue === undefined) {
                      cellValue = "";
                    } else if (typeof cellValue === "object") {
                      cellValue = JSON.stringify(cellValue);
                    }

                    cell.value = cellValue;
                  }
                }
              );

              // Commit the row to ensure it's properly written
              try {
                targetRow.commit();
              } catch (commitError) {
                addProgressMessage(
                  `Warning: Error committing row ${targetRowNumber}: ${commitError.message}`,
                  "warning"
                );
              }
            });

            addProgressMessage(
              `Successfully added ${dataToProcess.length} rows to ${stepData.excelSheet} sheet with preserved formatting`,
              "success"
            );
          } catch (addError) {
            addProgressMessage(
              `Error adding data to ${stepData.excelSheet}: ${addError.message}`,
              "error"
            );
          }
        }
      });

      // Validate the workbook before writing
      addProgressMessage(
        "Validating workbook structure before writing...",
        "info"
      );

      try {
        // Try to validate the workbook structure
        workbook.worksheets.forEach((ws, index) => {
          try {
            if (ws.name) {
              addProgressMessage(
                `Sheet ${index + 1}: "${ws.name}" - ${ws.rowCount} rows`,
                "info"
              );
            }
          } catch (sheetError) {
            addProgressMessage(
              `Warning: Issue with sheet ${index + 1}: ${sheetError.message}`,
              "warning"
            );
          }
        });
      } catch (validationError) {
        addProgressMessage(
          `Validation warning: ${validationError.message}`,
          "warning"
        );
      }

      // Generate file with conservative options to avoid corruption
      const excelBuffer = await workbook.xlsx.writeBuffer({
        useSharedStrings: false, // Try without shared strings first
        useStyles: true,
      });

      addProgressMessage(
        `Generated Excel buffer of size: ${excelBuffer.byteLength} bytes`,
        "info"
      );

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `WxCC_Template_AWDB_Import_${getFormattedCurrentDateYYYYMMDD()}.xlsx`;
      saveAs(blob, fileName);

      addProgressMessage(
        `Excel template generated with preserved styling: ${fileName}`,
        "success"
      );
    } catch (error) {
      addProgressMessage(
        `Error generating Excel template: ${error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Generate Excel file using a styling-preserving approach (commented out - not currently used)
  /*
  const generateExcelTemplate = async () => {
    if (Object.keys(collectedData).length === 0) {
      addProgressMessage(
        "No data collected yet. Please collect at least one dataset.",
        "error"
      );
      return;
    }

    setLoading(true);
    addProgressMessage(
      "Generating Excel template with collected data...",
      "info"
    );

    try {
      // Load the original template using fetch and XLSX with preservation options
      const templatePath = "/wxcc_files/wxcc_mock_ddd.xlsx";
      const response = await fetch(templatePath);

      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      // Read with options to preserve as much as possible
      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
        cellStyles: true,
        cellHTML: false,
        cellFormula: true,
        cellDates: true,
        sheetStubs: false,
      });

      addProgressMessage(
        `Template loaded with sheets: ${workbook.SheetNames.join(", ")}`,
        "success"
      );

      // Process each collected dataset
      Object.entries(collectedData).forEach(([stepKey, stepData]) => {
        addProgressMessage(
          `Checking step: ${stepKey}, completed: ${stepData.completed}, excelSheet: ${stepData.excelSheet}, finalProcessed: ${stepData.finalProcessed}`,
          "info"
        );

        if (
          stepData.completed &&
          stepData.excelSheet &&
          (stepData.transformedData || stepData.data)
        ) {
          addProgressMessage(`Processing ${stepData.name} data...`, "info");

          const worksheet = workbook.Sheets[stepData.excelSheet];
          if (!worksheet) {
            addProgressMessage(
              `Warning: Sheet "${
                stepData.excelSheet
              }" not found. Available sheets: ${workbook.SheetNames.join(
                ", "
              )}`,
              "warning"
            );
            return;
          }

          // Use finalProcessed data if available, otherwise use regular transformedData or data
          const dataToProcess = stepData.finalProcessed
            ? stepData.data
            : stepData.transformedData || stepData.data;

          addProgressMessage(
            `Using ${
              stepData.finalProcessed ? "finalProcessed" : "regular"
            } data. Available data types: transformedData=${!!stepData.transformedData}, data=${!!stepData.data}`,
            "info"
          );
          addProgressMessage(
            `Data to process: ${JSON.stringify(
              dataToProcess?.slice(0, 2) || []
            )} (showing first 2 rows)`,
            "info"
          );

          if (!dataToProcess || dataToProcess.length === 0) {
            addProgressMessage(
              `No data to process for ${stepData.name}`,
              "warning"
            );
            return;
          }

          // Use XLSX.utils.sheet_add_json which is better at preserving existing structure
          try {
            // Find the last row with data
            const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
            let lastRow = range.e.r;

            // Look for actual last row with data
            for (let r = range.e.r; r >= range.s.r; r--) {
              let hasData = false;
              for (let c = range.s.c; c <= range.e.c; c++) {
                const cellAddress = XLSX.utils.encode_cell({ r, c });
                if (
                  worksheet[cellAddress] &&
                  worksheet[cellAddress].v !== undefined &&
                  worksheet[cellAddress].v !== ""
                ) {
                  hasData = true;
                  break;
                }
              }
              if (hasData) {
                lastRow = r;
                break;
              }
            }

            addProgressMessage(
              `Adding data after row ${lastRow} in sheet ${stepData.excelSheet}`,
              "info"
            );

            // Convert data to match Excel column names
            const excelFormattedData = dataToProcess.map((row) => {
              const formattedRow = {};
              // Use the column mapping to transform data keys to Excel column names
              Object.entries(stepData.columnMapping).forEach(
                ([dataKey, excelColumn]) => {
                  if (row.hasOwnProperty(dataKey)) {
                    formattedRow[excelColumn] = row[dataKey] || "";
                  }
                }
              );

              // Log the mapping for debugging
              addProgressMessage(
                `Row mapping: ${JSON.stringify({
                  input: Object.keys(row),
                  output: Object.keys(formattedRow),
                })}`,
                "info"
              );
              return formattedRow;
            });

            addProgressMessage(
              `Excel formatted data sample: ${JSON.stringify(
                excelFormattedData.slice(0, 2)
              )}`,
              "info"
            );

            // Add data using cell-by-cell approach to better preserve formatting
            const sheetRange = XLSX.utils.decode_range(
              worksheet["!ref"] || "A1:A1"
            );

            // Find header row to get column positions
            let headerRow = -1;
            let columnPositions = {};

            for (
              let r = sheetRange.s.r;
              r <= Math.min(sheetRange.e.r, 20);
              r++
            ) {
              for (let c = sheetRange.s.c; c <= sheetRange.e.c; c++) {
                const cellAddress = XLSX.utils.encode_cell({ r, c });
                const cell = worksheet[cellAddress];
                if (cell && cell.v) {
                  const cellValue = cell.v.toString();
                  Object.keys(stepData.columnMapping).forEach((dataKey) => {
                    const excelColumn = stepData.columnMapping[dataKey];
                    if (cellValue === excelColumn) {
                      columnPositions[dataKey] = c;
                      headerRow = r;
                    }
                  });
                }
              }
            }

            addProgressMessage(
              `Found header row at ${headerRow}, column positions: ${JSON.stringify(
                columnPositions
              )}`,
              "info"
            );

            // Insert data row by row, cell by cell
            excelFormattedData.forEach((row, rowIndex) => {
              const targetRow = lastRow + 1 + rowIndex;
              Object.entries(row).forEach(([columnName, value]) => {
                // Find the data key that maps to this column name
                const dataKey = Object.keys(stepData.columnMapping).find(
                  (key) => stepData.columnMapping[key] === columnName
                );

                if (dataKey && columnPositions[dataKey] !== undefined) {
                  const cellAddress = XLSX.utils.encode_cell({
                    r: targetRow,
                    c: columnPositions[dataKey],
                  });

                  // Create cell with just the value, preserving any inherited formatting
                  worksheet[cellAddress] = {
                    v: value,
                    t: typeof value === "number" ? "n" : "s",
                  };
                }
              });

              // Update sheet range to include new data
              const newRange = XLSX.utils.encode_range({
                s: sheetRange.s,
                e: {
                  r: Math.max(sheetRange.e.r, targetRow),
                  c: sheetRange.e.c,
                },
              });
              worksheet["!ref"] = newRange;
            });

            addProgressMessage(
              `Successfully added ${dataToProcess.length} rows to ${stepData.excelSheet} sheet`,
              "success"
            );
          } catch (addError) {
            addProgressMessage(
              `Error adding data to ${stepData.excelSheet}: ${addError.message}`,
              "error"
            );
          }
        }
      });

      // Generate file with preservation options
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
        compression: true,
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `WxCC_Template_AWDB_Import_${getFormattedCurrentDateYYYYMMDD()}.xlsx`;
      saveAs(blob, fileName);

      addProgressMessage(
        `Excel template generated and downloaded: ${fileName}`,
        "success"
      );
    } catch (error) {
      addProgressMessage(
        `Error generating Excel template: ${error.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  */

  // Clear specific collected data
  const clearDataStep = (stepKey) => {
    setCollectedData((prev) => {
      const updated = { ...prev };
      delete updated[stepKey];
      return updated;
    });
    addProgressMessage(
      `Cleared data for ${dataCollectionSteps[stepKey].name}`,
      "info"
    );
  };

  // Clear all collected data
  const clearAllData = () => {
    setCollectedData({});
    setCurrentQuery(Object.keys(dataCollectionSteps)[0]);
    addProgressMessage("Cleared all collected data", "info");
  };

  const completedSteps = Object.keys(collectedData).filter(
    (key) => collectedData[key]?.completed
  );
  const totalSteps = Object.keys(dataCollectionSteps).length;

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "1400px",
        margin: "auto",
        fontFamily: "Inter, sans-serif",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "30px", textAlign: "center" }}>
        <h1 style={{ color: "#333", marginBottom: "10px", fontSize: "2.2em" }}>
          AWDB to WxCC Migration Tool
        </h1>
        <p style={{ color: "#666", marginBottom: "15px", fontSize: "1.1em" }}>
          Collect multiple datasets from AWDB and populate WxCC Excel template
        </p>
        {orgid ? (
          <p style={{ color: "#888", fontSize: "0.9em" }}>
            Authenticated with Org ID:{" "}
            <span
              style={{
                fontWeight: "bold",
                fontFamily: "monospace",
                backgroundColor: "#e9ecef",
                padding: "2px 6px",
                borderRadius: "3px",
              }}
            >
              {orgid}
            </span>
          </p>
        ) : (
          <p style={{ color: "#888", fontSize: "0.9em" }}>
            No authentication required - Processing SQL data only
          </p>
        )}
      </div>

      {/* Progress Overview */}
      <div
        style={{
          marginBottom: "25px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "20px",
          backgroundColor: "#fff",
        }}
      >
        <h2 style={{ fontSize: "1.3em", marginBottom: "15px", color: "#333" }}>
          Data Collection Progress ({completedSteps.length}/{totalSteps})
        </h2>

        {/* Progress Bar */}
        <div
          style={{
            width: "100%",
            backgroundColor: "#e9ecef",
            borderRadius: "10px",
            height: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: `${(completedSteps.length / totalSteps) * 100}%`,
              backgroundColor: "#28a745",
              height: "100%",
              borderRadius: "10px",
              transition: "width 0.3s ease",
            }}
          ></div>
        </div>

        {/* Data Collection Steps */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "15px",
          }}
        >
          {Object.entries(dataCollectionSteps).map(([stepKey, step]) => {
            const isCompleted = collectedData[stepKey]?.completed;
            const isCurrent = currentQuery === stepKey;
            const isSupplementary = step.isSupplementary;
            const hasComplexProcessing = collectedData[stepKey]?.finalProcessed;

            return (
              <div
                key={stepKey}
                style={{
                  padding: "15px",
                  border: `2px solid ${
                    isCompleted ? "#28a745" : isCurrent ? "#007bff" : "#dee2e6"
                  }`,
                  borderRadius: "8px",
                  backgroundColor: isCompleted
                    ? "#d4edda"
                    : isCurrent
                    ? "#e7f3ff"
                    : "#f8f9fa",
                  cursor: isCompleted ? "default" : "pointer",
                  transition: "all 0.3s ease",
                  opacity: isSupplementary ? 0.8 : 1,
                }}
                onClick={() => !isCompleted && setCurrentQuery(stepKey)}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: isCompleted
                        ? "#28a745"
                        : isCurrent
                        ? "#007bff"
                        : "#6c757d",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "10px",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    {isCompleted
                      ? "✓"
                      : Object.keys(dataCollectionSteps).indexOf(stepKey) + 1}
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      color: isCompleted
                        ? "#155724"
                        : isCurrent
                        ? "#004085"
                        : "#495057",
                      fontSize: "1.1em",
                    }}
                  >
                    {step.name}{" "}
                    {isSupplementary && (
                      <span style={{ fontSize: "0.8em", color: "#6c757d" }}>
                        (Support Data)
                      </span>
                    )}
                  </h3>
                </div>

                <p
                  style={{
                    margin: "0 0 10px 0",
                    color: "#666",
                    fontSize: "0.9em",
                  }}
                >
                  {step.description}
                </p>

                {isCompleted && (
                  <div style={{ fontSize: "0.85em", color: "#155724" }}>
                    <div>
                      ✅ {collectedData[stepKey].rowCount} rows collected
                    </div>
                    {hasComplexProcessing && (
                      <div style={{ color: "#0066cc", fontWeight: "bold" }}>
                        🔄 Enhanced with additional data
                      </div>
                    )}
                    <div style={{ fontSize: "0.8em", marginTop: "5px" }}>
                      {new Date(
                        collectedData[stepKey].collectedAt
                      ).toLocaleString()}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDataStep(stepKey);
                      }}
                      style={{
                        marginTop: "8px",
                        padding: "4px 8px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "0.8em",
                        cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}

                {isCurrent && !isCompleted && (
                  <div
                    style={{
                      fontSize: "0.85em",
                      color: "#004085",
                      fontWeight: "bold",
                    }}
                  >
                    📝 Current Step - Ready for data
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Import Mode Selection */}
      <div
        style={{
          marginBottom: "25px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "20px",
          backgroundColor: "#fff",
        }}
      >
        <div style={{
          backgroundColor: "#fff3cd",
          border: "1px solid #ffeaa7",
          borderRadius: "8px",
          padding: "15px",
          marginBottom: "20px",
          fontSize: "14px"
        }}>
          <h3 style={{ margin: "0 0 8px 0", color: "#856404" }}>Manual SQL Input Only</h3>
          <p style={{ margin: 0 }}>
            <strong>Copy and paste SQL query results</strong> directly into the text areas below. 
            All file upload functionality has been removed to ensure manual data verification and accuracy.
          </p>
        </div>

        {/* Test Data Loading Section */}
        <div style={{
          backgroundColor: "#d1ecf1",
          border: "1px solid #bee5eb",
          borderRadius: "8px",
          padding: "15px",
          marginBottom: "20px",
          fontSize: "14px"
        }}>
          <h3 style={{ margin: "0 0 8px 0", color: "#0c5460" }}>🧪 Testing Mode</h3>
          <p style={{ margin: "0 0 15px 0" }}>
            <strong>For faster testing:</strong> Load all your CSV files from the sql responses folder automatically.
          </p>
          <button
            onClick={loadTestData}
            disabled={loading}
            style={{
              padding: "10px 20px",
              backgroundColor: "#17a2b8",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
              fontSize: "14px",
              fontWeight: "bold"
            }}
          >
            {loading ? "Loading Test Data..." : "🚀 Load All Test CSV Files"}
          </button>
        </div>
      </div>


      {/* SQL Query Section */}
      {currentQuery && (
        <div
          style={{
            marginBottom: "25px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "20px",
            backgroundColor: "#fff",
          }}
        >
          <h2
            style={{ fontSize: "1.3em", marginBottom: "15px", color: "#333" }}
          >
            Current Step: {dataCollectionSteps[currentQuery].name}
          </h2>

          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h3 style={{ margin: "0", color: "#495057", fontSize: "1.1em" }}>
                Suggested SQL Query:
              </h3>
              <button
                onClick={() =>
                  copyToClipboard(dataCollectionSteps[currentQuery].sqlQuery)
                }
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "0.9em",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                title="Copy SQL query to clipboard"
              >
                📋 Copy Query
              </button>
            </div>
            <pre
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                padding: "15px",
                fontSize: "0.9em",
                overflow: "auto",
                margin: "0 0 15px 0",
                position: "relative",
              }}
            >
              {dataCollectionSteps[currentQuery].sqlQuery}
            </pre>
            <p style={{ color: "#666", fontSize: "0.9em", margin: 0 }}>
              Expected columns:{" "}
              <strong>
                {dataCollectionSteps[currentQuery].expectedColumns.join(", ")}
              </strong>
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
                color: "#444",
              }}
            >
              Paste SQL Results (CSV format):
            </label>
            <textarea
              value={sqlResults}
              onChange={(e) => setSqlResults(e.target.value)}
              placeholder="ReasonText,ReasonType
Break,1
Lunch,1
Meeting,2"
              rows={8}
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "0.9em",
                fontFamily: "monospace",
                resize: "vertical",
              }}
            />
          </div>

          <button
            onClick={processCurrentQuery}
            disabled={!sqlResults.trim()}
            style={{
              marginTop: "15px",
              padding: "12px 25px",
              backgroundColor: !sqlResults.trim() ? "#ccc" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: !sqlResults.trim() ? "not-allowed" : "pointer",
              fontSize: "1em",
              fontWeight: "600",
            }}
          >
            Collect This Data
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "15px",
          marginBottom: "30px",
        }}
      >
        <button
          onClick={generateExcelTemplateWithStyling}
          disabled={loading || Object.keys(collectedData).length === 0}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 25px",
            backgroundColor:
              loading || Object.keys(collectedData).length === 0
                ? "#ccc"
                : "#28a745",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor:
              loading || Object.keys(collectedData).length === 0
                ? "not-allowed"
                : "pointer",
            fontSize: "1em",
            fontWeight: "600",
            transition: "background-color 0.3s ease",
          }}
        >
          {loading ? "Generating..." : "Generate WxCC Template"}
        </button>

        <button
          onClick={clearAllData}
          disabled={Object.keys(collectedData).length === 0}
          style={{
            padding: "12px 25px",
            backgroundColor:
              Object.keys(collectedData).length === 0 ? "#ccc" : "#dc3545",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor:
              Object.keys(collectedData).length === 0
                ? "not-allowed"
                : "pointer",
            fontSize: "1em",
            fontWeight: "600",
          }}
        >
          Clear All Data
        </button>
      </div>

      {/* Progress Log */}
      <div
        style={{
          marginBottom: "25px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "20px",
          backgroundColor: "#fff",
        }}
      >
        <h2 style={{ fontSize: "1.2em", marginBottom: "15px", color: "#333" }}>
          Activity Log
        </h2>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "15px",
            borderRadius: "6px",
            height: "250px",
            overflowY: "auto",
            fontSize: "0.9em",
            border: "1px solid #e9ecef",
          }}
        >
          {progress.length === 0 ? (
            <p style={{ color: "#666", fontStyle: "italic" }}>
              No activity yet...
            </p>
          ) : (
            <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
              {progress.map((item, index) => (
                <li
                  key={index}
                  style={{
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      color: "#999",
                      fontSize: "0.8em",
                      fontFamily: "monospace",
                      flexShrink: 0,
                    }}
                  >
                    [{item.timestamp}]
                  </span>
                  <span
                    style={{
                      color:
                        item.type === "error"
                          ? "#dc3545"
                          : item.type === "success"
                          ? "#28a745"
                          : item.type === "warning"
                          ? "#ffc107"
                          : "#007bff",
                    }}
                  >
                    {item.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Back Button */}
      <div style={{ textAlign: "center", marginTop: "30px" }}>
        <button
          onClick={onBackToTools}
          style={{
            padding: "10px 20px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "0.9em",
            transition: "background-color 0.3s ease",
          }}
        >
          ← Back to Main Menu
        </button>
      </div>
    </div>
  );
};

export default SQLQueryProcessor;
